import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import apiClient from '../index';

// Fetch all staff notifications for the current user
export const useStaffNotifications = () => {
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading, isError } = useQuery({
    queryKey: ['staffNotifications'],
    queryFn: async () => {
      const response = await apiClient.get('/api/snotify');
      return response.data.data || [];
    },
    staleTime: 1000 * 60, // Consider data fresh for 1 minute
  });

  // Calculate unread count
  const unreadCount = notifications.filter(notif => !notif.read).length;

  // Mark a single notification as read
  const { mutate: markAsRead } = useMutation({
    mutationFn: async (notificationId) => {
      const response = await apiClient.patch(`/api/snotify/${notificationId}`, { read: true });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['staffNotifications']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to mark notification as read');
    }
  });

  // Mark all notifications as read
  const { mutate: markAllAsRead } = useMutation({
    mutationFn: async () => {
      // Since this endpoint doesn't exist in the backend, we'll mark each unread notification as read
      const unreadNotifications = notifications.filter(n => !n.read);
      await Promise.all(unreadNotifications.map(n => 
        apiClient.patch(`/api/snotify/${n.notificationId}`, { read: true })
      ));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['staffNotifications']);
      toast.success('All notifications marked as read');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to mark all notifications as read');
    }
  });

  // Clear all notifications
  const { mutate: clearNotifications } = useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete('/api/snotify');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['staffNotifications']);
      toast.success('All notifications cleared');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to clear notifications');
    }
  });

  // Remove a single notification
  const { mutate: removeNotification } = useMutation({
    mutationFn: async (notificationId) => {
      const response = await apiClient.delete(`/api/snotify/${notificationId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['staffNotifications']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete notification');
    }
  });

  return {
    staffNotifications: notifications,
    isLoading,
    isError,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    removeNotification
  };
};

// Fetch a single staff notification by ID
export const useGetStaffNotification = (notificationId) => {
  return useQuery({
    queryKey: ['staffNotification', notificationId],
    queryFn: async () => {
      if (!notificationId) return null;
      const response = await apiClient.get(`/api/snotify/${notificationId}`);
      return response.data;
    },
    enabled: !!notificationId,
  });
};